import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MenuIcon, SearchIcon, HeartIcon, BellIcon, PlusCircleIcon, XIcon, BookIcon, ShoppingCartIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { usePlatform } from '../../context/PlatformContext';
import PlatformToggle from '../UI/PlatformToggle';
interface MobileTopBarProps {
  isReader: boolean;
  showTopBar: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
  isSearchOpen: boolean;
  setIsSearchOpen: (isOpen: boolean) => void;
  cartItemCount: number;
}
const MobileTopBar: React.FC<MobileTopBarProps> = ({
  isReader,
  showTopBar,
  isMenuOpen,
  setIsMenuOpen,
  isSearchOpen,
  setIsSearchOpen,
  cartItemCount
}) => {
  const dk = useDarkClasses();
  const {
    isAfterTide
  } = usePlatform();
  const location = useLocation();
  const navigate = useNavigate();
  const isOnNotifications = location.pathname === '/notifications';
  return <header className={`fixed top-0 left-0 right-0 backdrop-blur-sm shadow-sm z-40 transition-transform duration-300 md:hidden pt-safe ${showTopBar ? 'transform-none' : 'transform -translate-y-full'} ${isAfterTide ? 'bg-[#1a1a2e]/95 border-b border-[#16213e]' : dk.isDark ? 'bg-gray-800/90' : 'bg-white/90'}`} data-id="element-2761" data-mp="30mottomeipem">
      <div className={`flex items-center justify-between h-16 px-4 ${isAfterTide ? 'bg-[#1a1a2e]' : 'bg-[#111827]'}`} data-id="element-2762" data-mp="2mvtg9z0yjk4p">
        {/* Left: Menu Icon or Back Button */}
        <div className="flex items-center" data-id="element-2763" data-mp="3fj640ciutxzl">
          {isMenuOpen || isSearchOpen ? <button onClick={() => {
          setIsMenuOpen(false);
          setIsSearchOpen(false);
        }} className="p-2 -ml-2 touch-target flex items-center justify-center active:scale-95 transition-transform" aria-label="Close" data-id="element-2764" data-mp="3ajtibv3pywyl">
              <XIcon className={`h-6 w-6 ${isAfterTide ? 'text-[#a0a0b0]' : dk.isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2765" data-mp="1u5tkcpofphdz" />
            </button> : <button onClick={() => setIsMenuOpen(true)} className="p-2 -ml-2 touch-target flex items-center justify-center active:scale-95 transition-transform" aria-label="Menu" data-id="element-2766" data-mp="20mv2m1p2b1b5">
              <MenuIcon className={`h-6 w-6 ${isAfterTide ? 'text-[#a0a0b0]' : dk.isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2767" data-mp="1rk3n9qt2fle8" />
            </button>}
        </div>

        {/* Center: Logo or Search */}
        <div className="flex-1 flex justify-center" data-id="element-2768" data-mp="13w2i8rv3in3k">
          {isSearchOpen ? <span className={`text-lg font-semibold ${isAfterTide ? 'text-[#e2b04a]' : dk.isDark ? 'text-white' : ''}`} data-id="element-2769" data-mp="13u9blckhg2vk">
              Search
            </span> : <PlatformToggle data-id="element-2770" data-mp="u092z0pzsfi3" />}
        </div>

        {/* Right: Context-specific icons */}
        <div className="flex items-center space-x-2" data-id="element-2771" data-mp="fb7n2q5ms00r">
          {!isSearchOpen && <>
              <button onClick={() => setIsSearchOpen(true)} className="p-2 touch-target flex items-center justify-center active:scale-95 transition-transform" aria-label="Search" data-id="element-2772" data-mp="ac4if6tkbvtb">
                <SearchIcon className={`h-5 w-5 ${isAfterTide ? 'text-[#a0a0b0]' : dk.isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2773" data-mp="k9hj12z6j4nf" />
              </button>
              <button onClick={() => {
            if (isOnNotifications) {
              // If we have history, go back. Otherwise go home.
              if (window.history.length > 2) {
                navigate(-1);
              } else {
                navigate('/');
              }
            } else {
              navigate('/notifications');
            }
          }} className="p-2 relative touch-target flex items-center justify-center active:scale-95 transition-transform" aria-label={isOnNotifications ? 'Close notifications' : 'Notifications'} data-id="element-2774" data-mp="t9xkk6wd41pn">
                {isOnNotifications ? <XIcon className={`h-6 w-6 ${isAfterTide ? 'text-[#e2b04a]' : isReader ? 'text-indigo-500' : 'text-purple-500'}`} data-id="element-2775" data-mp="3l6l6x1yx4zrv" /> : <>
                    <BellIcon className={`h-5 w-5 ${isAfterTide ? 'text-[#a0a0b0]' : dk.isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2776" data-mp="2she0zhx4x7wl" />
                    <span className={`absolute top-1 right-1 ${isAfterTide ? 'bg-[#e2b04a] text-[#1a1a2e]' : isReader ? 'bg-indigo-600 text-white' : 'bg-purple-600 text-white'} text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center`} data-id="element-2777" data-mp="31ilw5r4ctsmg">
                      3
                    </span>
                  </>}
              </button>
              {isReader ? <Link to="/wishlist" className="p-2 relative touch-target flex items-center justify-center active:scale-95 transition-transform" data-id="element-2778" data-mp="1x8k894okc9xq">
                  <HeartIcon className={`h-5 w-5 ${isAfterTide ? 'text-[#a0a0b0]' : dk.isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2779" data-mp="1nhxw5kg221js" />
                </Link> : <Link to="/vendors/manage" className="p-2 touch-target flex items-center justify-center active:scale-95 transition-transform" data-id="element-2780" data-mp="1dk1pb4u5hsrh">
                  <PlusCircleIcon className={`h-5 w-5 ${isAfterTide ? 'text-[#c77dba]' : 'text-purple-600'}`} data-id="element-2781" data-mp="14lg6r9ooidqp" />
                </Link>}
              <Link to="/cart" className="p-2 relative touch-target flex items-center justify-center active:scale-95 transition-transform" data-id="element-2782" data-mp="2yz7do1fmijig">
                <ShoppingCartIcon className={`h-5 w-5 ${isAfterTide ? 'text-[#a0a0b0]' : dk.isDark ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-2783" data-mp="3mtr9j41e2y39" />
                {cartItemCount > 0 && <span className={`absolute top-1 right-1 ${isAfterTide ? 'bg-[#e2b04a] text-[#1a1a2e]' : 'bg-purple-600 text-white'} text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center`} data-id="element-2784" data-mp="3e28renkvu87j">
                    {cartItemCount}
                  </span>}
              </Link>
            </>}
        </div>
      </div>
    </header>;
};
export default MobileTopBar;